import classNames from 'classnames';
import { NOTION_FILTER, COLLECTION_FILTER_CURRENT_ITEM_PREFIX, COLLECTION_FILTER_CURRENT_ITEM_PREFIX_COPY } from '@typedream/data';
import React, {
  useContext, useEffect, useState, useRef,
} from 'react';
import {
  ChevronDown, RefreshCcw, Trash, X,
} from '@styled-icons/feather';
import {
  VISITOR_FILTER_STYLE_OPTIONS, CollectionFilter, RootElement, VISITOR_FILTER_OPTION_ALL,
} from '@typedream/components';
import { isEqual, omit } from 'lodash';
import { ChromePicker, RGBColor } from 'react-color';
import { isHexColor, isRGBColor, isValidColor } from '@typedream/core';
import styles from '../CollectionSettings.module.css';
import {
  DEFAULT_MAX_ITEMS,
  NOTION_SORT_DIRECTION, NOTION_SORT_DIRECTION_TYPE,
  VISITOR_FILTER_MOCK,
  VISITOR_FILTER_COLUMN_ALLOWED_TYPE,
} from '../constants';
import ButtonBlack from '../../../../Button/ButtonBlack';
import { ICollectionData, ICollectionDataItemMetadataKeys } from '../../../../../types';
import { BLOCK_ELEMENT_KEY, NOTION_FILTER_TYPE, NOTION_KEYS } from '../../../types';
import { errToString } from '../../../../../utils/errors';
import { CollectionSettingsDataContext } from '../contextProvider';
import ToolbarDropdown from '../../SideToolbar/helpers/ToolbarDropdown';
import OptionsContainer from '../../../../Options';
import sharedStyles from '../../../styles/Shared.module.css';
import CheckboxItem from '../../../../Options/CheckboxItem';
import { DEFAULT_WIDTH_COLUMNS } from '../../SideToolbar/helpers/constants';
import LoadingSmall from '../../../../../components/LoadingSmall';
import { DEFAULT_ACCENT_NAME } from '../../../constants';
import { CustomizationContext } from '../../../context/CustomizationContextProvider';
import { backgroundColors, backgroundColorsBasic } from '../../../assets/colors';
import { getBackgroundColorStyle } from '../../../utils/getBackgroundColorStyle';
import useOutsideAlerter from '../../../utils/onClickOutside';
import { COLLECTION_COLUMN_NAME_PREFIX } from '../../../../../constants';
import { useSpaceAndPageIDFromRouter } from '../../../../../data/space/hooks';
import { useGetPageQuery } from '../../../../../store';
import CurrentItemFilter from './CurrentItemFilter';

type ColorPickerProps = {
  currentColor: string,
  onSave: (selectedColor: string) => void,
}

function ColorPicker(props: ColorPickerProps) {
  const { currentColor, onSave } = props;
  const { colors } = useContext(CustomizationContext);
  const [color, setColor] = useState((
    (isHexColor(currentColor) || isRGBColor(currentColor)) && currentColor
  ) || '');
  const [colorError, setColorError] = useState(null);
  const [openColorPicker, setOpenColorPicker] = useState(false);
  const [rgba, setrgba] = useState<RGBColor>({} as RGBColor);

  useEffect(() => {
    setColor((
      (isHexColor(currentColor) || isRGBColor(currentColor)) && currentColor
    ) || '');

    setOpenColorPicker(false);
  }, [currentColor]);

  useEffect(
    () => {
      if (!isEqual(rgba, {})) {
        setColor(`rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`);
      }
    },
    [rgba],
  );

  return (
    <div>
      <div className={styles.colorPickerContainer}>
        <button
          type="button"
          tabIndex={-1}
          onKeyDown={(_) => null}
          onClick={() => setOpenColorPicker(!openColorPicker)}
          className={styles.colorPickerTrigger}
          style={
            color
              ? { ...getBackgroundColorStyle(colors, color) }
              : {}
          }
        >
          ?
        </button>
        <input
          className={styles.colorPickerInput}
          style={{ borderColor: colorError ? 'red' : undefined }}
          type="text"
          placeholder="#FFF"
          value={color}
          onChange={(e) => {
            const { value } = e.target;

            // validate input
            setColor(value);
            if (isValidColor(value)) {
              setColorError(false);
            } else if (isValidColor(`#${value}`)) {
              setColor(`#${value}`);
              setColorError(false);
            } else if (value.length > 0) {
              setColorError(true);
            }
          }}
          onKeyDown={(e: any) => {
            if (e.key !== 'Enter' || colorError) return;
            e.preventDefault();
            onSave(color);
          }}
        />

        <button
          onClick={() => (!colorError ? onSave(color) : null)}
          type="button"
          className={classNames(
            styles.colorPickerSaveBtn,
            { [styles.colorPickerSaveBtnActive]: !colorError && !!color && color !== currentColor },
          )}
        >
          Save
        </button>
      </div>

      {openColorPicker && (
        <div
          style={{
            position: 'absolute', left: '100%', top: '0', zIndex: 1, padding: '16px',
          }}
        >
          <ChromePicker
            onChangeComplete={({ rgb }) => setrgba(rgb)}
            onChange={({ rgb }) => setrgba(rgb)}
            color={rgba}
          />
        </div>
      )}
    </div>
  );
}

function SortFilterNotion() {
  const [filterConditions, setfilterConditions] = useState([]);
  const [filterColumnsOpen, setfilterColumnsOpen] = useState(false);
  const [filtersOpen, setfiltersOpen] = useState(false);
  const [currentItemDropdownOpen, setCurrentItemDropdownOpen] = useState(false);
  const [maxItemsViewAll, setmaxItemsViewAll] = useState(false);
  const [maxItems, setmaxItems] = useState<number>(DEFAULT_MAX_ITEMS);
  const [filterColumn, setfilterColumn] = useState(undefined);
  const [filterColumnType, setfilterColumnType] = useState(undefined);
  const [filterValue, setfilterValue] = useState(undefined);
  const [filterCondition, setfilterCondition] = useState(undefined);
  const [sortColumnsOpen, setsortColumnsOpen] = useState(false);
  const [sortDirectionOpen, setsortDirectionOpen] = useState(false);
  const [sortColumn, setsortColumn] = useState(undefined);
  const [sortDirection, setsortDirection] = useState(NOTION_SORT_DIRECTION_TYPE.ACSENDING);
  const [isVisitorFilterColumnShown, setIsVisitorFilterColumnShown] = useState(false);
  const [visitorFilterColumn, setVisitorFilterColumn] = useState(undefined);
  const [visitorFilterColumnType, setVisitorFilterColumnType] = useState(undefined);
  const [visitorFilterStyle, setVisitorFilterStyle] = useState<VISITOR_FILTER_STYLE_OPTIONS>(
    VISITOR_FILTER_STYLE_OPTIONS.PILL,
  );
  const [spaceID, pageID] = useSpaceAndPageIDFromRouter();
  const { data: page } = useGetPageQuery([spaceID, pageID]);
  const itemPageData = page?.customization?.item_page_data;

  const { colors } = useContext(CustomizationContext);
  const BACKGROUND_COLORS = { ...colors, ...backgroundColors };
  const visitorFilterColorOptions = omit({
    [DEFAULT_ACCENT_NAME]: BACKGROUND_COLORS[DEFAULT_ACCENT_NAME],
    ...backgroundColorsBasic,
  }, ['default']);

  const [visitorFilterColor, setVisitorFilterColor] = useState(
    BACKGROUND_COLORS[DEFAULT_ACCENT_NAME],
  );
  const [isVisitorFilterColorShown, setIsVisitorFilterColorShown] = useState(false);

  const {
    collectionData,
    element,
    seterr,
    updatePageCollectionData,
    columns,
    editorSyncData,
    listColumns,
    tables,
    tableConnected,
    loadingListColumns,
    err,
  } = useContext(CollectionSettingsDataContext);

  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const onClickOutside = () => {
    setCurrentItemDropdownOpen(false);
  };
  useOutsideAlerter(dropdownMenuRef, null, onClickOutside);

  const listFilterConditions = () => {
    const columnType = columns[filterColumn]?.type;
    const availableFilters = NOTION_FILTER[columnType];
    setfilterColumnType(columnType);
    setfilterConditions(availableFilters);
    if (availableFilters) {
      const defaultFilter = Object.keys(availableFilters)?.[0];
      setfilterCondition(defaultFilter);
      setfilterValue(null);
    }
  };

  const visitorListItem = Object.entries(columns)
    .filter(([k, v]) => VISITOR_FILTER_COLUMN_ALLOWED_TYPE.findIndex((t) => t === v.type) !== -1)
    .map(([k, v]) => k);

  const oriMaxItemsViewAll = !collectionData?.metadata?.[ICollectionDataItemMetadataKeys.MAX_ITEMS];
  const oriMaxItems = collectionData?.metadata?.[ICollectionDataItemMetadataKeys.MAX_ITEMS];
  const oriFilterColumn = collectionData?.metadata?.[ICollectionDataItemMetadataKeys.FILTER_COLUMN];
  const oriFilterCondition = collectionData?.metadata?.[
    ICollectionDataItemMetadataKeys.FILTER_CONDITION];
  const oriFilterValue = collectionData?.metadata?.[ICollectionDataItemMetadataKeys.FILTER_VALUE];
  const oriSortColumn = collectionData?.metadata?.[ICollectionDataItemMetadataKeys.SORT_COLUMN];
  const oriSortDirection = collectionData?.metadata?.[
    ICollectionDataItemMetadataKeys.SORT_DIRECTION] as NOTION_SORT_DIRECTION_TYPE;
  const oriVisitorFilterColumn = collectionData?.metadata?.
    [ICollectionDataItemMetadataKeys.VISITOR_FILTER_COLUMN];
  const oriVisitorFilterStyle = collectionData?.metadata?.
    [ICollectionDataItemMetadataKeys.VISITOR_FILTER_STYLE] as VISITOR_FILTER_STYLE_OPTIONS;
  const oriVisitorFilterColor = collectionData?.metadata?.
    [ICollectionDataItemMetadataKeys.VISITOR_FILTER_COLOR];

  const removeFilter = () => {
    setfilterColumn(undefined);
    setfilterColumnType(undefined);
    setfilterCondition(undefined);
    setfilterValue(undefined);
  };

  const removeSort = () => {
    setsortColumn(undefined);
    setsortDirection(NOTION_SORT_DIRECTION_TYPE.ACSENDING);
  };

  const removeVisitorFilter = () => {
    setVisitorFilterColumn(undefined);
    setVisitorFilterColumnType(undefined);
    setVisitorFilterStyle(VISITOR_FILTER_STYLE_OPTIONS.PILL);
    setVisitorFilterColor(DEFAULT_ACCENT_NAME);
  };

  const onCancelFilters = () => {
    if (collectionData) {
      setmaxItemsViewAll(oriMaxItemsViewAll);
      setmaxItems(oriMaxItems);
      setfilterColumn(oriFilterColumn);
      setfilterCondition(oriFilterCondition);
      setfilterValue(oriFilterValue);
    } else {
      setmaxItemsViewAll(false);
      setmaxItems(null);
      setfilterColumn(undefined);
      setfilterColumnType(undefined);
      setfilterCondition(undefined);
      setfilterValue(undefined);
    }
  };

  const onCancelSort = () => {
    if (collectionData) {
      setsortColumn(oriSortColumn);
      setsortDirection(oriSortDirection || NOTION_SORT_DIRECTION_TYPE.ACSENDING);
    } else {
      removeSort();
    }
  };

  const onCancelVisitorFilter = () => {
    if (collectionData) {
      setVisitorFilterColumn(oriVisitorFilterColumn);
      setVisitorFilterStyle(oriVisitorFilterStyle || VISITOR_FILTER_STYLE_OPTIONS.PILL);
      setVisitorFilterColor(oriVisitorFilterColor || DEFAULT_ACCENT_NAME);
    } else {
      removeVisitorFilter();
    }
  };

  useEffect(() => {
    if (collectionData) {
      onCancelFilters();
      onCancelSort();
      onCancelVisitorFilter();
    }
  }, [collectionData]);

  useEffect(() => {
    listFilterConditions();
  }, [filterColumn, columns]);

  useEffect(() => {
    if (visitorFilterColumn) {
      const columnType = columns[visitorFilterColumn]?.type;
      setVisitorFilterColumnType(columnType);
    }
  }, [visitorFilterColumn]);

  const listColumnNames = async () => {
    try {
      await listColumns(collectionData);
      onCancelFilters();
      onCancelSort();
    } catch (e) {
      seterr(errToString(e));
    }
  };

  useEffect(() => {
    listColumnNames();
  }, [collectionData]);

  const updateFilter = async () => {
    seterr(null);
    if (filterConditions?.[filterCondition] === NOTION_FILTER_TYPE.NUMBER
      && !filterValue.startsWith(COLLECTION_FILTER_CURRENT_ITEM_PREFIX_COPY)
      && Number.isNaN(Number(filterValue))) {
      seterr('Please input valid number');
      return;
    }
    const newCollectionData = { ...collectionData };
    const connectionDataID = element?.[BLOCK_ELEMENT_KEY.COLLECTION_DATA_ID];
    newCollectionData.metadata = {
      ...newCollectionData.metadata,
      [ICollectionDataItemMetadataKeys.MAX_ITEMS]: maxItemsViewAll ? undefined : maxItems,
      [ICollectionDataItemMetadataKeys.FILTER_COLUMN]: filterColumn,
      [ICollectionDataItemMetadataKeys.FILTER_COLUMN_TYPE]: filterColumnType,
      [ICollectionDataItemMetadataKeys.FILTER_CONDITION]: filterCondition,
      [ICollectionDataItemMetadataKeys.FILTER_VALUE]:
      (typeof filterValue !== 'string') ? filterValue
        : filterValue?.replaceAll(
          COLLECTION_FILTER_CURRENT_ITEM_PREFIX_COPY,
          COLLECTION_FILTER_CURRENT_ITEM_PREFIX,
        ),
    };
    const newConnData: ICollectionData = {
      [connectionDataID]: newCollectionData,
    };
    try {
      await updatePageCollectionData(newConnData);
      await editorSyncData(newCollectionData);
    } catch (e) {
      seterr(errToString(e));
    }
  };

  const updateSorting = async () => {
    const newCollectionData = { ...collectionData };
    const connectionDataID = element?.[BLOCK_ELEMENT_KEY.COLLECTION_DATA_ID];
    newCollectionData.metadata = {
      ...newCollectionData.metadata,
      [ICollectionDataItemMetadataKeys.SORT_COLUMN]: sortColumn,
      [ICollectionDataItemMetadataKeys.SORT_DIRECTION]: sortColumn ? sortDirection : undefined,
    };
    const newConnData: ICollectionData = {
      [connectionDataID]: newCollectionData,
    };
    try {
      await updatePageCollectionData(newConnData);
      await editorSyncData(newCollectionData);
    } catch (e) {
      seterr(errToString(e));
    }
  };

  const updateVisitorFilter = async () => {
    const newCollectionData = { ...collectionData };
    const connectionDataID = element?.[BLOCK_ELEMENT_KEY.COLLECTION_DATA_ID];
    newCollectionData.metadata = {
      ...newCollectionData.metadata,
      [ICollectionDataItemMetadataKeys.VISITOR_FILTER_COLUMN]: visitorFilterColumn,
      [ICollectionDataItemMetadataKeys.VISITOR_FILTER_STYLE]: visitorFilterColumn
        ? visitorFilterStyle : undefined,
      [ICollectionDataItemMetadataKeys.VISITOR_FILTER_COLOR]: visitorFilterColumn
        ? visitorFilterColor : undefined,
      [ICollectionDataItemMetadataKeys.VISITOR_FILTER_COLUMN_TYPE]: visitorFilterColumn
        ? visitorFilterColumnType : undefined,
    };
    const newConnData: ICollectionData = {
      [connectionDataID]: newCollectionData,
    };
    try {
      await updatePageCollectionData(newConnData);
      await editorSyncData(newCollectionData);
    } catch (e) {
      seterr(errToString(e));
    }
  };

  if (!tables
    || !tableConnected
    || !collectionData
    || !collectionData?.[NOTION_KEYS.TABLE_ID]) {
    return (
      <div className={classNames(styles.block, 'pb-6')}>
        <div className={classNames('is-size-4 with-text-color mb-3')}>
          Filters
        </div>
        <div className={classNames(styles.subtitleContainer, 'is-subtitle with-light-text-color mb-1')}>
          Please select a table first.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={classNames('is-size-4 with-text-color mb-3')}>
        Filters
      </div>
      <div className={classNames(styles.block, 'pb-6')}>
        <div className={classNames(styles.subtitleContainer, 'with-text-color has-text-weight-bold mb-1')}>
          Max Number of Items
        </div>
        <div className={classNames(styles.subtitleContainer, 'is-subtitle with-light-text-color mb-1')}>
          This is the maximum number of items to be shown in this collection.
        </div>
        <input
          type="number"
          className={classNames(sharedStyles.inputBig, 'mb-2')}
          value={maxItemsViewAll ? '' : maxItems}
          placeholder="e.g. 3"
          disabled={maxItemsViewAll}
          onChange={(e) => setmaxItems(parseInt(e.target.value, 10))}
          onBlur={() => {
            if (!maxItems) {
              setmaxItemsViewAll(true);
            }
          }}
        />
        <div className="mb-5">
          <CheckboxItem
            title={(
              <p className="with-text-color">
                View All
                {' '}
                <span className="with-lighter-text-color">(Max 100 items at a time)</span>
              </p>
            )}
            selected={maxItemsViewAll}
            onClick={() => setmaxItemsViewAll((prev) => !prev)}
            className={styles.checkboxItem}
            checkboxSize={13}
          />
        </div>
        <div className={classNames(styles.subtitleContainer, 'with-text-color has-text-weight-bold mb-1')}>
          Filter
        </div>
        <div className={classNames(styles.subtitleContainer, 'is-subtitle with-light-text-color mb-1')}>
          Only show items that matches this filter.

        </div>

        <div className={classNames(styles.subtitleContainer, 'with-text-color mb-1 mt-3')}>
          Where...
        </div>
        <div className="is-flex is-align-items-center">
          <ToolbarDropdown
            dropup={false}
            width={DEFAULT_WIDTH_COLUMNS}
            open={filterColumnsOpen}
            setopen={setfilterColumnsOpen}
            triggerClassname="m-0 p-0"
            contentClassname="m-0 pt-2"
            trigger={(
              <OptionsContainer
                selected={false}
                className="m-0"
              >
                {filterColumn ? <div className="with-text-color">{filterColumn}</div> : <div className="with-lighter-text-color">Select Column</div>}
                <ChevronDown size={15} className="ml-2" />
              </OptionsContainer>
            )}
            content={(
              <div style={{
                maxHeight: 300,
                overflowY: 'scroll',
                marginBottom: 20,
              }}
              >
                {Object.keys(columns)?.length > 0
                  ? Object.keys(columns)?.map((c) => (
                    <div
                      className={classNames(styles.options, 'with-text-color')}
                      role="none"
                      onClick={() => {
                        setfilterColumn(c);
                        setfilterColumnsOpen(false);
                      }}
                    >
                      {c}
                    </div>
                  ))
                  : (
                    <div
                      className={classNames(styles.options, 'with-text-color')}
                    >
                      <div className="is-subtitle">
                        You have not shared any tables with Typedream
                      </div>
                      <div className="with-accent-text-color">See image &rarr;</div>
                    </div>
                  )}
              </div>
            )}
          />
          <ToolbarDropdown
            dropup={false}
            width={DEFAULT_WIDTH_COLUMNS}
            open={filtersOpen}
            setopen={setfiltersOpen}
            triggerClassname="m-0 p-0"
            contentClassname="m-0 pt-2"
            trigger={(
              <OptionsContainer
                selected={false}
                className="m-0"
              >
                {filterCondition ? <div className="with-text-color">{filterCondition.replaceAll('_', ' ')}</div> : <div className="with-lighter-text-color">Select a Filter</div>}
                <ChevronDown size={15} className="ml-2" />
              </OptionsContainer>
            )}
            content={(
              <div style={{
                maxHeight: 300,
                overflowY: 'scroll',
                marginBottom: 20,
              }}
              >
                {filterConditions && Object.keys(filterConditions).length > 0
                  ? Object.keys(filterConditions).map((f) => (
                    <div
                      className={classNames(styles.options, 'with-text-color')}
                      role="none"
                      onClick={() => {
                        setfilterCondition(f);
                        setfiltersOpen(false);
                      }}
                    >
                      {f.replaceAll('_', ' ')}
                    </div>
                  ))
                  : (
                    <div
                      className={classNames(styles.options, 'with-text-color')}
                    >
                      <div className="is-subtitle">
                        Can not find a filter for this column type
                      </div>
                    </div>
                  )}
              </div>
            )}
          />

          {
            filterConditions?.[filterCondition] === NOTION_FILTER_TYPE.STRING
            || filterConditions?.[filterCondition] === NOTION_FILTER_TYPE.NUMBER
              ? (
                <div style={{ position: 'relative', width: 300 }}>
                  <input
                    type="text"
                    className={classNames(sharedStyles.inputOptionsContainer, {
                      [sharedStyles.inputOptionsContainerHighlightValue]:
                      filterValue?.startsWith(COLLECTION_FILTER_CURRENT_ITEM_PREFIX_COPY)
                      || filterValue?.startsWith(COLLECTION_FILTER_CURRENT_ITEM_PREFIX),
                    })}
                    value={filterValue?.replaceAll(
                      COLLECTION_FILTER_CURRENT_ITEM_PREFIX,
                      COLLECTION_FILTER_CURRENT_ITEM_PREFIX_COPY,
                    )}
                    placeholder={filterConditions?.[filterCondition] === NOTION_FILTER_TYPE.STRING ? 'Value' : 'Number Value'}
                    onChange={(e) => {
                      if (!filterValue?.startsWith(COLLECTION_FILTER_CURRENT_ITEM_PREFIX_COPY)
                      && !filterValue?.startsWith(COLLECTION_FILTER_CURRENT_ITEM_PREFIX)) {
                        setCurrentItemDropdownOpen(true);
                        setfilterValue(e.target.value);
                      }
                    }}
                    onFocus={() => { setCurrentItemDropdownOpen(true); }}
                  />
                  {Boolean(filterValue)
                    && (
                    <div
                      className="with-accent-text-color with-cursor-pointer with-background-color"
                      role="none"
                      style={{ position: 'absolute', top: 12, right: 12 }}
                      onClick={() => { setfilterValue(''); }}
                    >
                      <X size={20} />
                    </div>
                    )}
                  {itemPageData && currentItemDropdownOpen && (
                  <CurrentItemFilter
                    columns={Object.keys(columns).filter(
                      (col) => !col.startsWith(COLLECTION_COLUMN_NAME_PREFIX),
                    )}
                    containerRef={dropdownMenuRef}
                    handleSelected={(col) => {
                      setfilterValue(col);
                      setCurrentItemDropdownOpen(false);
                    }}
                  />
                  )}
                </div>
              ) : null
          }
          {
            filterConditions?.[filterCondition] === NOTION_FILTER_TYPE.BOOLEAN
              ? (
                <OptionsContainer
                  selected={filterValue}
                  className={classNames('m-0 has-fit-content-width', styles.inputOptionsContainer)}
                  onClick={() => {
                    setfilterValue((val) => !val);
                  }}
                >
                  <CheckboxItem
                    title="Checked"
                    selected={filterValue}
                    className={styles.checkboxItem}
                    checkboxSize={13}
                  />

                </OptionsContainer>
              ) : null
          }
          <div
            className="with-accent-text-color with-cursor-pointer"
            role="none"
            onClick={listColumnNames}
          >
            <RefreshCcw size={20} className="ml-3" />
          </div>
          {
            loadingListColumns && (
              <LoadingSmall className="ml-1" loading={loadingListColumns} />
            )
          }
          {
            (filterColumn || filterCondition) && (
              <div
                className="with-accent-text-color with-cursor-pointer"
                role="none"
                onClick={removeFilter}
              >
                <Trash size={20} className="ml-3" />
              </div>
            )
          }
        </div>
        <div className="is-flex is-flex-direction-row is-align-items-center mt-5">
          <ButtonBlack
            disabled={
              maxItems === oriMaxItems
              && maxItemsViewAll === oriMaxItemsViewAll
              && filterColumn === oriFilterColumn
              && filterCondition === oriFilterCondition
              && filterValue === oriFilterValue
            }
            onClick={updateFilter}
          >
            Save Filter &rarr;
          </ButtonBlack>
          <div className="with-lighter-text-color with-cursor-pointer ml-3" role="none" onClick={onCancelFilters}>Cancel</div>
        </div>
        <div className="has-text-danger mt-2">{err}</div>
      </div>

      <hr className={sharedStyles.hr} />
      {/* VISITOR FILTER */}
      <div className="py-5">
        <div className={classNames('is-size-4 with-text-color mb-3')}>
          Filter tabs for visitors
        </div>
        <div className={classNames(styles.subtitleContainer, 'is-subtitle with-light-text-color mb-6')}>
          Create filtered tabs for your web visitors
        </div>
        <div className={classNames(styles.subtitleContainer, 'with-text-color has-text-weight-bold mb-1')}>
          Filter by columns
        </div>
        <div className={classNames(styles.subtitleContainer, 'is-subtitle with-light-text-color mb-1')}>
          You can only use columns with the &quot;select&quot;
          or &quot;multi-select&quot; type as a filter
        </div>
        <div className="is-flex is-align-items-center mb-5">
          <ToolbarDropdown
            dropup={false}
            width={DEFAULT_WIDTH_COLUMNS}
            open={isVisitorFilterColumnShown}
            setopen={setIsVisitorFilterColumnShown}
            triggerClassname="m-0 p-0"
            contentClassname="m-0 pt-2"
            trigger={(
              <OptionsContainer
                selected={false}
                className="m-0"
              >
                {visitorFilterColumn ? <div className="with-text-color">{visitorFilterColumn}</div> : <div className="with-lighter-text-color">Select Column</div>}
                <ChevronDown size={15} className="ml-2" />
              </OptionsContainer>
            )}
            content={(
              <div style={{
                maxHeight: 300,
                overflowY: 'scroll',
                marginBottom: 20,
              }}
              >
                {visitorListItem.length > 0
                  ? visitorListItem.map((c) => (
                    <div
                      className={classNames(styles.options, 'with-text-color')}
                      role="none"
                      onClick={() => {
                        setVisitorFilterColumn(c);
                        setIsVisitorFilterColumnShown(false);
                      }}
                    >
                      {c}
                    </div>
                  ))
                  : (
                    <div
                      className={classNames(styles.options, 'with-text-color')}
                    >
                      <div className="is-subtitle">
                        You have not shared any tables with Typedream
                      </div>
                      <div className="with-accent-text-color">See image &rarr;</div>
                    </div>
                  )}
              </div>
            )}
          />
          <div
            className="with-accent-text-color with-cursor-pointer"
            role="none"
            onClick={listColumnNames}
          >
            <RefreshCcw size={20} className="ml-3" />
          </div>
          {
            loadingListColumns && (
              <LoadingSmall className="ml-1" loading={loadingListColumns} />
            )
          }
          {
            visitorFilterColumn && (
              <div
                className="with-accent-text-color with-cursor-pointer"
                role="none"
                onClick={removeVisitorFilter}
              >
                <Trash size={20} className="ml-3" />
              </div>
            )
          }
        </div>
        {visitorFilterColumn
        && (
        <>
          <div className={classNames(styles.subtitleContainer, 'with-text-color has-text-weight-bold mb-1')}>
            Tab Style
          </div>
          <div className="mb-5">
            {/* TODO: Clarify why RootElement here???
                If it is just for getting correct style,
                then having div.typedream.content should be enough
             */}
            <RootElement element={{ id: '', type: 'root', children: [] }}>
              <div style={{ width: '100%', padding: 0 }}>
                <div className={styles.filterStyleOptionsContainer}>
                  {Object.values(VISITOR_FILTER_STYLE_OPTIONS).map(
                    (filterStyle: VISITOR_FILTER_STYLE_OPTIONS) => (
                      <button
                        className={classNames(styles.filterStyleOptions, {
                          [styles.filterStyleOptionsActive]: filterStyle === visitorFilterStyle,
                        })}
                        key={filterStyle}
                        onClick={() => { setVisitorFilterStyle(filterStyle); }}
                        type="button"
                      >
                        <div className={classNames(
                          styles.filterStyleOptionsPreviewContainer,
                          {
                            [styles.filterStyleOptionsPreviewContainer_leftColumn]:
                          filterStyle === VISITOR_FILTER_STYLE_OPTIONS.LEFT_COLUMN,
                          },
                        )}
                        >
                          <CollectionFilter
                            filters={VISITOR_FILTER_MOCK}
                            filterStyle={filterStyle}
                            filterColor={visitorFilterColor}
                            setSelectedFilter={() => { /** do nothing */ }}
                            selectedFilter={VISITOR_FILTER_OPTION_ALL}
                          />
                        </div>
                      </button>
                    ),
                  )}
                </div>
              </div>
            </RootElement>
          </div>

          <div className={classNames(styles.subtitleContainer, 'with-text-color has-text-weight-bold mb-1')}>
            Tab Color
          </div>
          <div>
            <ToolbarDropdown
              dropup={false}
              width={DEFAULT_WIDTH_COLUMNS}
              open={isVisitorFilterColorShown}
              setopen={setIsVisitorFilterColorShown}
              triggerClassname="m-0 p-0"
              contentClassname="m-0 pt-2"
              trigger={(
                <OptionsContainer
                  selected={false}
                  className="m-0"
                >
                  <div className="is-flex is-align-items-center">
                    <div
                      className={classNames(styles.colorShowcase)}
                      style={{ background: visitorFilterColorOptions[visitorFilterColor] }}
                    />
                    {visitorFilterColor ? <div className="with-text-color">{visitorFilterColor}</div> : <div className="with-lighter-text-color">Select Tab Color</div>}
                  </div>
                  <ChevronDown size={15} className="ml-2" />
                </OptionsContainer>
            )}
              content={(
                <div style={{
                  maxHeight: 300,
                  overflowY: 'scroll',
                  marginBottom: 20,
                }}
                >
                  {Object.entries(visitorFilterColorOptions).map(([k, v]) => (
                    <div
                      onMouseDown={() => {
                        setVisitorFilterColor(k);
                        setIsVisitorFilterColorShown(false);
                      }}
                      role="none"
                      className={classNames(styles.options, 'with-text-color is-flex is-align-items-center')}
                    >
                      <div className={styles.colorShowcase} style={{ background: v }} />
                      {k}
                    </div>
                  ))}
                  <div className="px-3 pb-4">
                    <div className="mb-1 with-text-color is-size-6">
                      Custom Color
                    </div>
                    <ColorPicker
                      currentColor={visitorFilterColor}
                      onSave={(color) => {
                        setVisitorFilterColor(color);
                        setIsVisitorFilterColorShown(false);
                      }}
                    />
                  </div>
                </div>
            )}
            />
          </div>
        </>
        )}
        <div className="is-flex is-flex-direction-row is-align-items-center mt-5">
          <ButtonBlack
            disabled={
              visitorFilterColumn === oriVisitorFilterColumn
              && visitorFilterStyle === oriVisitorFilterStyle
              && visitorFilterColor === oriVisitorFilterColor
            }
            onClick={updateVisitorFilter}
          >
            Save filter tabs &rarr;
          </ButtonBlack>
          <div className="with-lighter-text-color with-cursor-pointer ml-3" role="none" onClick={onCancelVisitorFilter}>Cancel</div>
        </div>
        <div className="has-text-danger mt-2">{err}</div>
      </div>
      <hr className={sharedStyles.hr} />

      {/* SORT */}
      <div className={classNames('is-size-4 with-text-color mb-5 mt-6')}>
        Sorting
      </div>
      <div className={classNames(styles.block, 'pb-6')}>
        <div className={classNames(styles.subtitleContainer, 'with-text-color has-text-weight-bold mb-1')}>
          Sort items by
        </div>

        <div className="is-flex is-align-items-center">
          <ToolbarDropdown
            dropup={false}
            width={DEFAULT_WIDTH_COLUMNS}
            open={sortColumnsOpen}
            setopen={setsortColumnsOpen}
            triggerClassname="m-0 p-0"
            contentClassname="m-0 pt-2"
            trigger={(
              <OptionsContainer
                selected={false}
                className="m-0"
              >
                {sortColumn ? <div className="with-text-color">{sortColumn}</div> : <div className="with-lighter-text-color">Select Column</div>}
                <ChevronDown size={15} className="ml-2" />
              </OptionsContainer>
            )}
            content={(
              <div style={{
                maxHeight: 300,
                overflowY: 'scroll',
                marginBottom: 20,
              }}
              >
                {Object.keys(columns)?.length > 0
                  ? Object.keys(columns)?.map((c) => (
                    <div
                      className={classNames(styles.options, 'with-text-color')}
                      role="none"
                      onClick={() => {
                        setsortColumn(c);
                        setsortColumnsOpen(false);
                      }}
                    >
                      {c}
                    </div>
                  ))
                  : (
                    <div
                      className={classNames(styles.options, 'with-text-color')}
                    >
                      <div className="is-subtitle">
                        You have not shared any tables with Typedream
                      </div>
                      <div className="with-accent-text-color">See image &rarr;</div>
                    </div>
                  )}
              </div>
            )}
          />

          <ToolbarDropdown
            dropup={false}
            width={DEFAULT_WIDTH_COLUMNS}
            open={sortDirectionOpen}
            setopen={setsortDirectionOpen}
            triggerClassname="m-0 p-0"
            contentClassname="m-0 pt-2"
            trigger={(
              <OptionsContainer
                selected={false}
                className="m-0"
              >
                {sortColumn ? <div className="with-text-color">{NOTION_SORT_DIRECTION[sortDirection]}</div> : <div className="with-lighter-text-color">Direction</div>}
                <ChevronDown size={15} className="ml-2" />
              </OptionsContainer>
            )}
            content={(
              <div style={{
                maxHeight: 300,
                overflowY: 'scroll',
                marginBottom: 20,
              }}
              >
                {Object.keys(NOTION_SORT_DIRECTION)?.map((c: NOTION_SORT_DIRECTION_TYPE) => (
                  <div
                    className={classNames(styles.options, 'with-text-color')}
                    role="none"
                    onClick={() => {
                      setsortDirection(c);
                      setsortDirectionOpen(false);
                    }}
                  >
                    {NOTION_SORT_DIRECTION[c]}
                  </div>
                ))}
              </div>
            )}
          />

          <div
            className="with-accent-text-color with-cursor-pointer"
            role="none"
            onClick={listColumnNames}
          >
            <RefreshCcw size={20} className="ml-3" />
          </div>
          {
            loadingListColumns && (
              <LoadingSmall className="ml-1" loading={loadingListColumns} />
            )
          }
          {
            sortColumn && (
              <div
                className="with-accent-text-color with-cursor-pointer"
                role="none"
                onClick={removeSort}
              >
                <Trash size={20} className="ml-3" />
              </div>
            )
          }
        </div>
        <div className="is-flex is-flex-direction-row is-align-items-center mt-5">
          <ButtonBlack
            disabled={
              sortColumn === oriSortColumn
              && sortDirection === oriSortDirection
            }
            onClick={updateSorting}
          >
            Save Sorting &rarr;
          </ButtonBlack>
          <div className="with-lighter-text-color with-cursor-pointer ml-3" role="none" onClick={onCancelSort}>Cancel</div>
        </div>
        <div className="has-text-danger mt-2">{err}</div>
      </div>
    </>
  );
}

export default SortFilterNotion;
